import { ApiError, apiFetch, parseErrorDetail } from "@/api/fetch";
import type {
  CliExecuteResponse,
  MapProgress,
  ProteinMeta,
  SubgraphResponse,
} from "../types";
import { parseLineNumbers, stripAnsi } from "../utils/parseOutputs";
import { parseSqlTable } from "../utils/parseSqlTable";

/** Run a paperclip command through the backend CLI gateway. */
export const executeCli = async (
  command: string,
  raw: string,
): Promise<CliExecuteResponse> => {
  const response = await apiFetch("/cli/execute", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ command, raw }),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<CliExecuteResponse>;
};

/**
 * Run a long command (map) with `?stream=1` NDJSON progress. Progress lines
 * are surfaced through `onProgress`; the final `result` line is returned as a
 * normal execute response.
 */
export const executeCliStream = async (
  command: string,
  raw: string,
  onProgress: (progress: MapProgress) => void,
): Promise<CliExecuteResponse> => {
  const response = await apiFetch("/cli/execute?stream=1", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ command, raw }),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  if (!response.body) {
    return response.json() as Promise<CliExecuteResponse>;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let final: CliExecuteResponse | null = null;

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.type === "progress") {
        onProgress({
          completed: parsed.completed ?? 0,
          failed: parsed.failed ?? 0,
          in_progress: parsed.in_progress ?? 0,
          total: parsed.total ?? 0,
          elapsed_s: parsed.elapsed_s ?? 0,
        });
      } else if (parsed.type === "result" || parsed.output !== undefined) {
        final = parsed as CliExecuteResponse;
      }
    } catch {
      // Non-JSON line (shouldn't happen) — ignore.
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    lines.forEach(handleLine);
  }
  handleLine(buffer);

  if (!final) {
    throw new ApiError("Map stream ended without a result", 502);
  }
  return final;
};

/**
 * Ask the console agent to turn a natural-language request into a paperclip
 * command. Plain-text chunks stream through `onToken` as the model writes;
 * resolves with the full command once the stream ends.
 */
export const streamAgentCommand = async (
  prompt: string,
  history: string[],
  onToken: (token: string) => void,
): Promise<string> => {
  const response = await apiFetch("/cli/agent-command", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt, history }),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  if (!response.body) {
    const text = await response.text();
    onToken(text);
    return text;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let full = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    if (chunk) {
      full += chunk;
      onToken(chunk);
    }
  }
  return full;
};

/** UniProt accessions are short alphanumerics (P43220, A0A024R161); anything
 * else is refused before being interpolated into a SQL literal. */
const assertAccession = (accession: string): string => {
  if (!/^[A-Za-z0-9_-]{1,24}$/.test(accession)) {
    throw new ApiError(`Invalid protein accession: ${accession}`, 400);
  }
  return accession;
};

/** The exact CLI commands behind each section of the protein profile panel —
 * exported so the UI can show users what to run themselves. */
export const proteinCommands = {
  meta: (accession: string) => `cat /proteins/${accession}/meta.json`,
  structures: (accession: string) =>
    `sql -s proteins "SELECT DISTINCT entry_id, exptl_method, resolution, release_date FROM pdb_v.structures_by_accession WHERE accession='${accession}' ORDER BY release_date DESC LIMIT 25"`,
  drugs: (accession: string) =>
    `sql -s proteins "SELECT DISTINCT drug_name, max_phase, first_approval, action_type, mechanism_of_action FROM chembl_v.drugs_by_accession WHERE accession='${accession}' ORDER BY max_phase DESC, first_approval DESC NULLS LAST LIMIT 25"`,
};

/** Fetch UniProt entry metadata via `cat /proteins/{acc}/meta.json`. */
export const fetchProteinMeta = async (
  accession: string,
): Promise<ProteinMeta> => {
  const acc = assertAccession(accession);
  const result = await executeCli("cat", `/proteins/${acc}/meta.json`);
  if (result.exit_code !== 0) {
    throw new ApiError(stripAnsi(result.output).trim() || "Protein not found", 404);
  }
  const parsed = JSON.parse(stripAnsi(result.output)) as ProteinMeta &
    Record<string, unknown>;
  delete parsed._timing; // CLI session timing, not UniProt data
  return parsed;
};

const runProteinSql = async (
  raw: string,
): Promise<Array<Record<string, string | null>>> => {
  const result = await executeCli("sql", raw);
  if (result.exit_code !== 0) {
    throw new ApiError(stripAnsi(result.output).trim() || "Query failed", 500);
  }
  return parseSqlTable(result.output);
};

/** Experimental 3D structures for a protein (PDB, joined on accession). */
export const fetchProteinStructures = (accession: string) =>
  runProteinSql(
    proteinCommands.structures(assertAccession(accession)).replace(/^sql /, ""),
  );

/** Drugs and clinical candidates targeting a protein (ChEMBL mechanisms). */
export const fetchProteinDrugs = (accession: string) =>
  runProteinSql(
    proteinCommands.drugs(assertAccession(accession)).replace(/^sql /, ""),
  );

/**
 * Find the line numbers inside one paper that match a grep pattern, so the
 * detail panel can highlight the exact blocks. Returns [] on any failure —
 * highlights are a nice-to-have, not worth breaking the panel over.
 */
/**
 * Fetch a paper's local citation neighborhood for the graph view:
 * incoming citers, outgoing references, and the cross-citation edges
 * among the displayed nodes.
 */
export const fetchSubgraph = async (
  paperId: string,
  perSide: number,
): Promise<SubgraphResponse> => {
  const response = await apiFetch("/citegraph/subgraph", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: paperId, per_side: perSide }),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<SubgraphResponse>;
};

export const fetchGrepLineNumbers = async (
  docId: string,
  pattern: string,
  ignoreCase = false,
): Promise<number[]> => {
  try {
    const escaped = `"${pattern.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
    const result = await executeCli(
      "grep",
      `${escaped}${ignoreCase ? " -i" : ""} /papers/${docId}/content.lines`,
    );
    return parseLineNumbers(result.output);
  } catch {
    return [];
  }
};
